'use client';

import classnames from 'classnames';
import { useLocale } from 'next-intl';

import { Badge, BadgeVariant, LockIcon, RenderHTML } from '@shared/ui-components';
import {
  formatTranslationStringOnClient,
  GTM_ACTION_CLICK,
  GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
  GTM_EVENT_USER_INTERACTIONS,
  sendGtmEvent,
} from '@shared/utils';

import { CardLink, useUserContext } from '../../../../../components';
import { convertSecondsToMinutes } from '../../../../../utils/helpers/convert-seconds-to-minutes';
import { getCardLinkDescription } from '../../../../../utils/helpers/get-card-link-description';
import { getContentFallbackImageSource } from '../../../../../utils/helpers/get-content-fallback-image-source';
import { getContentTypeIconComponent } from '../../../../../utils/helpers/get-content-type-icon-component';
import { getFeaturedPeopleOrAuthors } from '../../../../../utils/helpers/get-featured-people-or-authors';
import { getRelativePathWithoutLocale } from '../../../../../utils/helpers/get-relative-path-without-locale';
import { getTextFromHtmlString } from '../../../../../utils/helpers/get-text-from-html-string';
import { type LearnCarouselCardData } from '../../../actions/types';
import { type LearnGenericTextsCmsContent } from '../../../graphql/get-learn-generic-texts-cms-content';

import { LearnContentCarouselCardImage } from './learn-content-carousel-card-image-with-number';
import { useLockedResourcePrompt } from './locked-resource-prompt';
import styles from './learn-content-carousel-card.module.scss';

export type LearnContentCarouselCardProps = {
  card: LearnCarouselCardData;
  carouselTitle: string;
  cmsContent: LearnGenericTextsCmsContent;
  selectedFilterValue?: string | null;
  svgNumber?: number | undefined;
};

export const LearnContentCarouselCard = ({
  card,
  carouselTitle,
  cmsContent,
  selectedFilterValue,
  svgNumber,
}: LearnContentCarouselCardProps) => {
  const { isAuthenticated } = useUserContext();
  const ContentTypeIconComponent = getContentTypeIconComponent(card?.ContentTypeSelection);
  const fallbackImageSource = getContentFallbackImageSource(card?.ContentTypeSelection);
  const imageAlt = card?.ThumbnailImage?.AltText ?? card?.Heading ?? '';
  const people = getFeaturedPeopleOrAuthors(card);
  const firstAuthor = people[0];
  const otherAuthorsCount = firstAuthor && people.length > 1 ? people.length - 1 : undefined;
  const description = getCardLinkDescription(card?.SubheadingText, card?.Subheading);
  const locale = useLocale();

  const formattedUrl = card?.RelativePath
    ? getRelativePathWithoutLocale(card?.RelativePath, locale)
    : undefined;

  const formattedReadTimeLabel =
    card.ReadTimeInSeconds && card.ReadTimeInSeconds > 0
      ? formatTranslationStringOnClient(cmsContent.minutesLabel, {
          readTime: convertSecondsToMinutes(card.ReadTimeInSeconds),
        })
      : null;

  const isLocked = card.membersOnlyContent;
  const { onResourceClick, renderLockedResourcePrompt, shouldLockedResourcePromptBeShown } =
    useLockedResourcePrompt({
      cmsContent,
      isResourceLocked: isLocked,
      resourceUrl: card?.RelativePath,
    });

  const sendLockedResourceIntoGTM = () => {
    const _url = process.env.NEXT_PUBLIC_BASE_URL || '';
    const allAuthors: string = people?.join(', ') || '';
    const _labelText =
      card?.Heading +
      ' ' +
      allAuthors +
      ' ' +
      formattedReadTimeLabel +
      ' ' +
      getTextFromHtmlString(description);

    sendGtmEvent({
      action: GTM_ACTION_CLICK,
      carousel_header: carouselTitle,
      event: GTM_EVENT_USER_INTERACTIONS,
      eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
      label: 'locked_resource_click',
      link_label: _labelText,
      link_url: _url + formattedUrl,
    });
  };

  return (
    <>
      {renderLockedResourcePrompt()}
      {card.isRandom && <Badge className={styles['random-item-indicator']}>Random</Badge>}
      <CardLink
        ImageComponent={
          <LearnContentCarouselCardImage
            alt={imageAlt}
            callToActionText={cmsContent.callToSignInLabel}
            fallbackSrc={fallbackImageSource}
            isAuthenticated={isAuthenticated}
            isLocked={isLocked}
            numberTranslation={cmsContent.number}
            src={card?.ThumbnailImage?.ImageReference?.Url ?? ''}
            svgNumber={svgNumber}
          />
        }
        actionClassName={classnames(
          styles['learn-content-carousel-card__action'],
          'card-with-hover-effect'
        )}
        className={classnames(styles['learn-content-carousel-card'])}
        data-ga-carousel-filter={selectedFilterValue}
        data-ga-carousel-header={carouselTitle}
        onClick={() => {
          onResourceClick();
          if (isLocked) sendLockedResourceIntoGTM();
        }}
        scroll
        to={shouldLockedResourcePromptBeShown ? undefined : formattedUrl ?? undefined}
      >
        <div className={styles['learn-content-carousel-card__type-badge-container']}>
          <Badge>
            <ContentTypeIconComponent
              className={styles['learn-content-carousel-card__type-icon']}
            />
          </Badge>
          {isLocked && (
            <Badge variant={BadgeVariant.Tertiary}>
              <LockIcon className={styles['learn-content-carousel-card__lock-icon']} />
              <span className={styles['learn-content-carousel-card__type-badge-text']}>
                {cmsContent.membersOnlyLabel}
              </span>
            </Badge>
          )}
        </div>
        {/* TODO: implementation in future */}
        {/* <BookmarkIcon className={styles['learn-content-carousel-card__bookmark-icon']} /> */}
        <div className={styles['learn-content-carousel-card__content']}>
          {card?.Heading && (
            <h3 className={styles['learn-content-carousel-card__title']} title={card.Heading}>
              {card.Heading}
            </h3>
          )}
          <div className={styles['learn-content-carousel-card__info']}>
            <p className={styles['learn-content-carousel-card__authors']} title={people.join('; ')}>
              <span className={styles['learn-content-carousel-card__first-author']}>
                {firstAuthor}
              </span>
              {otherAuthorsCount && (
                <span className={styles['learn-content-carousel-card__author-count']}>
                  +{otherAuthorsCount}
                </span>
              )}
            </p>
            {formattedReadTimeLabel && (
              <span
                className={classnames(styles['learn-content-carousel-card__read-time'], {
                  [styles['learn-content-carousel-card__read-time--with-separator']]: firstAuthor,
                })}
                title={formattedReadTimeLabel}
              >
                {formattedReadTimeLabel}
              </span>
            )}
          </div>
          <RenderHTML
            className={styles['learn-content-carousel-card__description']}
            html={description}
            title={getTextFromHtmlString(description)}
          />
        </div>
      </CardLink>
    </>
  );
};
